import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Box, Typography, Card, Grid, Container, CircularProgress, Alert, Chip, TextField, InputAdornment, IconButton, Button, Avatar, useMediaQuery, useTheme, CardActionArea, CardMedia, CardContent, Modal, Backdrop, Fade, Snackbar, Divider, Stack, Tooltip, Rating } from '@mui/material';
import { alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Close';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FilterListIcon from '@mui/icons-material/FilterList';
import ShareIcon from '@mui/icons-material/Share';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkIcon from '@mui/icons-material/Link';
import CloseIcon from '@mui/icons-material/Close';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import StarIcon from '@mui/icons-material/Star';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import SendIcon from '@mui/icons-material/Send';
import { useCart } from './CartContext';
import { fetchBranches, fetchBranchCities, fetchFeaturedBranches, toggleBranchLike, submitBranchRating, fetchUserRatingForBranch, fetchUserLikeForBranch } from './branchesApi';
import { fetchRestaurantInfo } from './api';
import { fetchFilters } from './api/filtersApi';
import config from './config';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ImageSlider from './ImageSlider';
import { QRCodeCanvas } from 'qrcode.react';

const deg2rad = (deg) => deg * (Math.PI / 180);

const calculateDistance = (lat1, lon1, lat2, lon2) => {
  if (!lat1 || !lon1 || !lat2 || !lon2) return null;
  const R = 6371;
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) + Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.sin(dLon/2) * Math.sin(dLon/2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

// ✅ NEW: Sort helper — IsFeatured DESC, DisplayOrder ASC, Id ASC (matches backend)
const getDisplayOrder = (b) => {
  const v = b?.displayOrder ?? b?.DisplayOrder;
  return (v === null || v === undefined || isNaN(Number(v))) ? 999999 : Number(v);
};
const getIsFeatured = (b) => Boolean(b?.isFeatured ?? b?.IsFeatured);
const getBranchId = (b) => Number(b?.id ?? b?.Id ?? 0);
const sortByDisplayOrder = (list) => {
  return [...list].sort((a, b) => {
    const featA = getIsFeatured(a) ? 1 : 0;
    const featB = getIsFeatured(b) ? 1 : 0;
    if (featA !== featB) return featB - featA;
    const oA = getDisplayOrder(a);
    const oB = getDisplayOrder(b);
    if (oA !== oB) return oA - oB;
    return getBranchId(a) - getBranchId(b);
  });
};

const BranchSelectionPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [branches, setBranches] = useState([]);
  const [logoUrl, setLogoUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { setSelectedBranch } = useCart();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [userLocation, setUserLocation] = useState(null);
  const [view, setView] = useState('filters');
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [filterSearch, setFilterSearch] = useState("");
  const [storeSearch, setStoreSearch] = useState("");
  const [filters, setFilters] = useState([]);
  const location = useLocation();
  const hasProcessedProductLinkRef = useRef(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedBranchForQR, setSelectedBranchForQR] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [branchCities, setBranchCities] = useState({});
  const [cityModalOpen, setCityModalOpen] = useState(false);
  const [blockedStoreName, setBlockedStoreName] = useState('');

  const [featuredBranches, setFeaturedBranches] = useState([]);
  const [userLikes, setUserLikes] = useState({});
  const [currentUserId, setCurrentUserId] = useState(null);
  const [ratingModalOpen, setRatingModalOpen] = useState(false);
  const [ratingBranch, setRatingBranch] = useState(null);
  const [ratingValue, setRatingValue] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const [submittingRating, setSubmittingRating] = useState(false);

  const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });
  const generateBranchLink = (branchId) => `${window.location.origin}/branch/${branchId}`;

  useEffect(() => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      if (userData?.id || userData?.Id) setCurrentUserId(userData.id || userData.Id);
    } catch {}
  }, []);

  const fallbackCopyToClipboard = (text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.top = '-9999px';
    textArea.style.left = '-9999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy') ? showSnackbar(t('branch.linkCopied', 'Link copied to clipboard!'), 'success') : showSnackbar(t('errors.copyFailed', 'Failed to copy link'), 'error');
    } catch (err) {
      console.error('Fallback copy failed:', err);
      showSnackbar(t('errors.copyFailed', 'Failed to copy link'), 'error');
    }
    document.body.removeChild(textArea);
  };

  const handleCopyLink = (branch) => {
    const link = generateBranchLink(branch.Id || branch.id);
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(link).then(() => showSnackbar(t('branch.linkCopied', 'Link copied to clipboard!'), 'success')).catch(() => fallbackCopyToClipboard(link));
    } else { fallbackCopyToClipboard(link); }
  };

  const handleShare = async (branch) => {
    const link = generateBranchLink(branch.Id || branch.id);
    const title = branch.name || branch.Name;
    if (navigator.share) {
      try {
        await navigator.share({ title, text: `${t('branch.checkOut', 'Check out')} ${title}!`, url: link });
        showSnackbar(t('branch.shared', 'Shared successfully!'), 'success');
      } catch (err) { if (err.name !== 'AbortError') handleCopyLink(branch); }
    } else { handleCopyLink(branch); }
  };

  const handleOpenQRModal = (branch) => { setSelectedBranchForQR(branch); setQrModalOpen(true); };

  const handleDownloadQR = () => {
    const canvas = document.getElementById('qr-code-canvas');
    if (canvas) {
      const url = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = url;
      link.download = `${selectedBranchForQR?.name || 'branch'}-qr-code.png`;
      link.click();
      showSnackbar(t('branch.qrDownloaded', 'QR Code downloaded!'), 'success');
    }
  };

  const handleShareWhatsApp = (branch) => { const link = generateBranchLink(branch.Id || branch.id); window.open(`https://wa.me/?text=${encodeURIComponent(`${t('branch.checkOut', 'Check out')} ${branch.name || branch.Name}! ${link}`)}`, '_blank'); };
  const handleShareFacebook = (branch) => { window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(generateBranchLink(branch.Id || branch.id))}`, '_blank'); };
  const handleShareTwitter = (branch) => { const link = generateBranchLink(branch.Id || branch.id); window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(`${t('branch.checkOut', 'Check out')} ${branch.name || branch.Name}!`)}&url=${encodeURIComponent(link)}`, '_blank'); };

  // ✅ FIX: handleLike now re-sorts after state update
  const handleLike = useCallback(async (branch, type) => {
    if (!currentUserId) {
      showSnackbar(t('branch.loginToInteract', 'Please log in to interact'), 'warning');
      return;
    }
    try {
      const storeId = branch.id || branch.Id;
      const result = await toggleBranchLike(storeId, { userId: currentUserId, type });
      setUserLikes(prev => ({ ...prev, [storeId]: result.userStatus }));
      setBranches(prev => sortByDisplayOrder(prev.map(b => {
        const bId = b.id || b.Id;
        return bId === storeId
          ? { ...b, totalLikes: result.branchStats.likes, TotalLikes: result.branchStats.likes, totalDislikes: result.branchStats.dislikes, TotalDislikes: result.branchStats.dislikes }
          : b;
      })));
      showSnackbar(result.message || t('branch.interactionSaved', 'Saved!'), 'success');
    } catch {
      showSnackbar(t('errors.interactionFailed', 'Failed to save interaction'), 'error');
    }
  }, [currentUserId, t]);

  const handleOpenRatingModal = useCallback(async (branch) => {
    if (!currentUserId) {
      showSnackbar(t('branch.loginToRate', 'Please log in to rate'), 'warning');
      return;
    }
    setRatingBranch(branch);
    setRatingValue(0);
    setRatingComment('');
    try {
      const storeId = branch.id || branch.Id;
      const existing = await fetchUserRatingForBranch(storeId, currentUserId);
      if (existing.hasRated && existing.rating) {
        setRatingValue(existing.rating.Rating || existing.rating.rating || 0);
        setRatingComment(existing.rating.Comment || existing.rating.comment || '');
      }
    } catch {}
    setRatingModalOpen(true);
  }, [currentUserId, t]);

  // ✅ FIX: handleSubmitRating now re-sorts after state update
  const handleSubmitRating = useCallback(async () => {
    if (!ratingBranch || !currentUserId || ratingValue === 0) return;
    setSubmittingRating(true);
    try {
      const storeId = ratingBranch.id || ratingBranch.Id;
      const result = await submitBranchRating(storeId, {
        userId: currentUserId,
        rating: ratingValue,
        comment: ratingComment.trim() || undefined,
      });
      setBranches(prev => sortByDisplayOrder(prev.map(b => {
        const bId = b.id || b.Id;
        return bId === storeId
          ? { ...b, averageRating: result.branchStats.averageRating, AverageRating: result.branchStats.averageRating, totalRatings: result.branchStats.totalRatings, TotalRatings: result.branchStats.totalRatings }
          : b;
      })));
      setRatingModalOpen(false);
      showSnackbar(t('branch.ratingSuccess', 'Thank you! Your rating has been saved.'), 'success');
    } catch {
      showSnackbar(t('errors.ratingFailed', 'Failed to save rating'), 'error');
    } finally {
      setSubmittingRating(false);
    }
  }, [ratingBranch, currentUserId, ratingValue, ratingComment, t]);

  const scrollToContent = () => {
    setTimeout(() => {
      const element = document.getElementById('store-selection-content');
      if (element) { element.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
      else { window.scrollTo({ top: isMobile ? 150 : 200, behavior: 'smooth' }); }
    }, 100);
  };

  const loadUserLikesInBackground = useCallback(async (branchList, userId) => {
    const BATCH_SIZE = 5;
    for (let i = 0; i < branchList.length; i += BATCH_SIZE) {
      const batch = branchList.slice(i, i + BATCH_SIZE);
      const batchResults = {};
      await Promise.all(
        batch.map(async (branch) => {
          try {
            const bId = branch.id || branch.Id;
            const result = await fetchUserLikeForBranch(bId, userId);
            batchResults[bId] = result.type || null;
          } catch {
            batchResults[branch.id || branch.Id] = null;
          }
        })
      );
      setUserLikes(prev => ({ ...prev, ...batchResults }));
    }
  }, []);

  const loadBranchCitiesInBackground = useCallback(async (branchList) => {
    const needsApiCall = [];
    const citiesFromData = {};

    branchList.forEach(branch => {
      const bId = branch.id || branch.Id;
      const embedded = branch.DeliveryCities || branch.deliveryCities || [];
      if (embedded.length > 0) {
        citiesFromData[bId] = embedded;
      } else {
        needsApiCall.push(branch);
      }
    });

    if (Object.keys(citiesFromData).length > 0) {
      setBranchCities(prev => ({ ...prev, ...citiesFromData }));
    }

    if (needsApiCall.length === 0) return;

    const BATCH_SIZE = 3;
    for (let i = 0; i < needsApiCall.length; i += BATCH_SIZE) {
      const batch = needsApiCall.slice(i, i + BATCH_SIZE);
      const batchResults = {};
      await Promise.all(
        batch.map(async (branch) => {
          try {
            const bId = branch.id || branch.Id;
            const cities = await fetchBranchCities(bId);
            batchResults[bId] = cities || [];
          } catch {
            batchResults[branch.id || branch.Id] = [];
          }
        })
      );
      setBranchCities(prev => ({ ...prev, ...batchResults }));
    }
  }, []);

  // ✅ FIX: Initial load now applies sortByDisplayOrder
  useEffect(() => {
    scrollToContent();
    const branchIdFromUrl = searchParams.get('branchId');
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => setUserLocation({ lat: position.coords.latitude, lng: position.coords.longitude }), (error) => console.log("Geolocation error:", error));
    }
    const getData = async () => {
      try {
        const [storesData, restaurantData, filtersData] = await Promise.all([fetchBranches(), fetchRestaurantInfo(), fetchFilters()]);

        // ✅ FIX #1: Sort branches on initial load
        const sortedData = sortByDisplayOrder(storesData || []);
        setBranches(sortedData);
        setFilters(filtersData || []);

        // ✅ FIX #2: Featured branches also sorted
        const featured = sortByDisplayOrder(
          sortedData.filter(b => b.isFeatured || b.IsFeatured)
        );
        setFeaturedBranches(featured);

        if (branchIdFromUrl && sortedData.length > 0) {
          const branch = sortedData.find(b => String(b.id) === String(branchIdFromUrl) || String(b.Id) === String(branchIdFromUrl));
          if (branch) { setSelectedBranch(branch); showSnackbar(t('branch.selected', `Selected: ${branch.name || branch.Name}`), 'success'); setTimeout(() => navigate('/'), 1000); }
        }
        if (restaurantData) {
          const logo = restaurantData.logoPath || restaurantData.LogoPath;
          if (logo) setLogoUrl(logo.startsWith('http') ? logo : `${config.SITE_BASE_URL}${logo.startsWith('/') ? '' : '/'}${logo}`);
        }

        setLoading(false);

        if (sortedData.length > 0) {
          loadBranchCitiesInBackground(sortedData);
        }
      } catch (err) { console.error("Failed to load data", err); setError(t('storeSelection.loadError', 'Failed to load stores')); setLoading(false); }
    };
    getData();
  }, [t, isMobile, searchParams, setSelectedBranch, navigate, loadBranchCitiesInBackground]);

  useEffect(() => {
    if (currentUserId && branches.length > 0) {
      loadUserLikesInBackground(branches, currentUserId);
    }
  }, [currentUserId, branches, loadUserLikesInBackground]);

  useEffect(() => {
    const handleProductLink = async () => {
      if (hasProcessedProductLinkRef.current) return;
      if (location.state?.selectedBranch && location.state?.fromProductLink && branches.length > 0) {
        hasProcessedProductLinkRef.current = true;
        const branch = location.state.selectedBranch;
        setSelectedBranch(branch);
        showSnackbar(`${t('branch.selected', 'Selected')}: ${branch.name || branch.Name}`, 'success');
        await new Promise(resolve => setTimeout(resolve, 300));
        navigate('/', { replace: true, state: null });
      }
    };
    if (!loading) handleProductLink();
  }, [branches, loading]);

  const handleFilterSelect = (filterId) => { setSelectedFilter(filterId); setView('stores'); setStoreSearch(''); scrollToContent(); };
  const handleBackToFilters = () => { setView('filters'); setSelectedFilter(null); setFilterSearch(''); scrollToContent(); };

  // ✅ FIX #3: storesWithDistance now respects DisplayOrder as primary sort,
  // distance only used as secondary info (not for reordering)
  const storesWithDistance = useMemo(() => {
    return branches.map(store => {
      let lat = store.latitude || store.Latitude, lng = store.longitude || store.Longitude;
      if (!lat && (store.mapLocation || store.MapLocation)) { const parts = (store.mapLocation || store.MapLocation).split(','); if (parts.length === 2) { lat = parseFloat(parts[0]); lng = parseFloat(parts[1]); } }
      return { ...store, distance: userLocation ? calculateDistance(userLocation.lat, userLocation.lng, lat, lng) : null };
    }).filter(store => (store.name || store.Name || '').toLowerCase().includes(storeSearch.trim().toLowerCase()));
    // ✅ NOTE: No longer sorting by distance here — DisplayOrder from branches state is preserved
  }, [branches, userLocation, storeSearch]);

  const onlyActiveStores = useMemo(() => storesWithDistance.filter(store => store.isActive || store.IsActive), [storesWithDistance]);

  // ✅ FIX #4: filteredStores always re-sorted to guarantee DisplayOrder
  const filteredStores = useMemo(() => {
    const result = selectedFilter != null
      ? onlyActiveStores.filter(store => (store.Filters || store.filters || []).some(f => String(f.Id) === String(selectedFilter)))
      : onlyActiveStores;
    return sortByDisplayOrder(result);
  }, [onlyActiveStores, selectedFilter]);

  const filteredFilters = useMemo(() => filters.filter(filter => filter.name.toLowerCase().includes(filterSearch.trim().toLowerCase())), [filters, filterSearch]);

  const handleSelectStore = (store) => {
    const storeId = store.id || store.Id;
    const cities = branchCities[storeId] || [];
    if (cities.length === 0) { setSelectedBranch(store); navigate('/'); return; }
    let userCity = '';
    try { const userData = JSON.parse(localStorage.getItem('user')); userCity = (userData?.city || userData?.City || userData?.cityName || userData?.CityName || '').trim().toLowerCase(); } catch {}
    if (!userCity) { setSelectedBranch(store); navigate('/'); return; }
    const delivers = cities.some(c => (c.Name || c.name || '').trim().toLowerCase() === userCity);
    if (delivers) { setSelectedBranch(store); navigate('/'); }
    else { setBlockedStoreName(store.name || store.Name); setCityModalOpen(true); }
  };

  const getImageUrls = (store) => {
    const imageUrl = store.ImageUrl || store.imageUrl || store.Image || store.image;
    const fallbackUrl = logoUrl || '/logo-storesye.png';
    if (!imageUrl) return { thumbnail: fallbackUrl, medium: fallbackUrl, large: fallbackUrl };
    if (typeof imageUrl === 'object' && imageUrl.thumbnail) {
      return { thumbnail: imageUrl.thumbnail.startsWith('http') ? imageUrl.thumbnail : `${config.SITE_BASE_URL}${imageUrl.thumbnail}`, medium: imageUrl.medium.startsWith('http') ? imageUrl.medium : `${config.SITE_BASE_URL}${imageUrl.medium}`, large: imageUrl.large.startsWith('http') ? imageUrl.large : `${config.SITE_BASE_URL}${imageUrl.large}` };
    }
    if (typeof imageUrl === 'string') {
      const fullUrl = (imageUrl.startsWith('http') || imageUrl.startsWith('data:image')) ? imageUrl : imageUrl.startsWith('/images/') ? `${config.SITE_BASE_URL}${imageUrl}` : imageUrl;
      return { thumbnail: fullUrl, medium: fullUrl, large: fullUrl };
    }
    return { thumbnail: fallbackUrl, medium: fallbackUrl, large: fallbackUrl };
  };

  if (loading) return (<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: 'background.default' }}><CircularProgress size={{ xs: 40, sm: 60 }} /></Box>);

  return (
    <>
      <ImageSlider />
      <Container id="store-selection-content" maxWidth="xl" sx={{ mt: { xs: 2, sm: 3, md: 4 }, mb: { xs: 4, sm: 5, md: 6 }, minHeight: '80vh', px: { xs: 1.5, sm: 2, md: 3 } }}>
        {error && <Alert severity="error" sx={{ mb: { xs: 2, sm: 3, md: 4 }, borderRadius: { xs: 1.5, sm: 2 } }}>{error}</Alert>}

        {/* ============= FILTERS VIEW ============= */}
        {view === 'filters' && (
          <>
            {featuredBranches.length > 0 && (
              <Box sx={{ mb: { xs: 3, sm: 4 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 1.5, sm: 2 }, px: { xs: 1, sm: 0 } }}>
                  <StarIcon sx={{ color: '#F59E0B', fontSize: { xs: 20, sm: 24 } }} />
                  <Typography variant="h6" sx={{ fontWeight: 800, fontSize: { xs: '1rem', sm: '1.2rem', md: '1.4rem' } }}>
                    {t('branch.featuredStores', '⭐ Featured Stores')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: { xs: 1.5, sm: 2 }, overflowX: 'auto', pb: 1, px: { xs: 1, sm: 0 }, '&::-webkit-scrollbar': { height: 6 }, '&::-webkit-scrollbar-thumb': { bgcolor: alpha(theme.palette.primary.main, 0.2), borderRadius: 3 } }}>
                  {featuredBranches.map((fb) => {
                    const fbImages = getImageUrls(fb);
                    const fbRating = fb.averageRating || fb.AverageRating || 0;
                    return (
                      <Card
                        key={`featured-${fb.id || fb.Id}`}
                        onClick={() => handleSelectStore(fb)}
                        sx={{ minWidth: { xs: 160, sm: 200, md: 240 }, maxWidth: { xs: 160, sm: 200, md: 240 }, cursor: 'pointer', borderRadius: { xs: 2, sm: 3 }, overflow: 'hidden', position: 'relative', flexShrink: 0, border: '2px solid', borderColor: '#F59E0B', transition: 'all 0.3s', '&:hover': { transform: 'translateY(-4px)', boxShadow: `0 8px 24px ${alpha('#F59E0B', 0.3)}` } }}
                      >
                        <Box sx={{ position: 'relative', width: '100%', paddingTop: '65%', overflow: 'hidden' }}>
                          <CardMedia component="img" image={fbImages.medium} alt={fb.name || fb.Name} loading="lazy" sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                          <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(transparent, rgba(0,0,0,0.7))', p: { xs: 1, sm: 1.5 } }}>
                            <Typography sx={{ color: 'white', fontWeight: 700, fontSize: { xs: '0.75rem', sm: '0.9rem' }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {fb.name || fb.Name}
                            </Typography>
                            {fbRating > 0 && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
                                <StarIcon sx={{ fontSize: { xs: 12, sm: 14 }, color: '#F59E0B' }} />
                                <Typography sx={{ color: 'white', fontSize: { xs: '0.65rem', sm: '0.75rem' }, fontWeight: 600 }}>
                                  {fbRating.toFixed(1)}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                        <Chip label={t('branch.featured', '⭐ Featured')} size="small" sx={{ position: 'absolute', top: 8, left: 8, bgcolor: '#F59E0B', color: 'white', fontWeight: 700, fontSize: { xs: '0.6rem', sm: '0.7rem' }, height: { xs: 20, sm: 24 } }} />
                      </Card>
                    );
                  })}
                </Box>
              </Box>
            )}

            <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 4, md: 5 } }}>
              <Typography variant="h3" component="h1" gutterBottom fontWeight="800" color="primary" sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem', lg: '3rem' }, mb: { xs: 1, sm: 1.5, md: 2 }, px: { xs: 1, sm: 0 } }}>
                {t('storeSelection.selectCategory', 'Select a Category')}
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem', lg: '1.25rem' }, mb: { xs: 2, sm: 2.5, md: 3 }, fontWeight: 400, px: { xs: 1, sm: 0 } }}>
                {t('storeSelection.categorySubtitle', 'Choose a category to view stores')}
              </Typography>
              <Box sx={{ mt: { xs: 2, sm: 2.5, md: 3 }, maxWidth: { xs: '100%', sm: 500 }, mx: 'auto', width: '100%', px: { xs: 1, sm: 0 } }}>
                <TextField
                  fullWidth variant="outlined" value={filterSearch} onChange={e => setFilterSearch(e.target.value)}
                  placeholder={t('storeSelection.searchFilters', 'Search categories...')} size={isMobile ? "small" : "medium"} autoComplete="off"
                  sx={{ backgroundColor: 'background.paper', borderRadius: { xs: 2, sm: 2.5, md: 3 }, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', '& .MuiOutlinedInput-root': { borderRadius: { xs: 2, sm: 2.5, md: 3 }, fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' }, fontWeight: 500, bgcolor: 'background.paper', transition: 'all 0.3s', '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.12)' }, '&.Mui-focused': { boxShadow: '0 4px 16px rgba(0,0,0,0.15)', borderColor: 'primary.main' } }, '& input': { py: { xs: 1.2, sm: 1.5, md: 2 } } }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><FilterListIcon color="primary" sx={{ fontSize: { xs: 20, sm: 24, md: 28 } }} /></InputAdornment>,
                    endAdornment: filterSearch && <InputAdornment position="end"><IconButton aria-label={t('storeSelection.clearSearch', 'Clear search')} onClick={() => setFilterSearch('')} edge="end" size={isMobile ? "small" : "medium"}><ClearIcon sx={{ fontSize: { xs: 18, sm: 22 } }} /></IconButton></InputAdornment>
                  }}
                />
              </Box>
            </Box>

            <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ justifyContent: 'center' }}>
              <Grid item xs={6} sm={6} md={4} lg={3}>
                <Card onClick={() => handleFilterSelect(null)} elevation={0} sx={{ height: { xs: 180, sm: 220, md: 260, lg: 280 }, display: 'flex', flexDirection: 'column', cursor: 'pointer', transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', borderRadius: { xs: 2, sm: 2.5, md: 3 }, overflow: 'hidden', border: '2px solid', borderColor: 'primary.main', bgcolor: 'primary.main', color: 'white', '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 24px rgba(0,0,0,0.2)' }, '&:active': { transform: 'scale(0.98)' } }}>
                  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.1)' }}>
                    <FilterListIcon sx={{ fontSize: { xs: 50, sm: 60, md: 70, lg: 80 }, opacity: 0.9 }} />
                  </Box>
                  <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5 }, textAlign: 'center', bgcolor: 'primary.dark' }}>
                    <Typography variant="h6" fontWeight="700" sx={{ fontSize: { xs: '0.85rem', sm: '1rem', md: '1.1rem', lg: '1.25rem' }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {t('storeSelection.allStores', 'All Stores')}
                    </Typography>
                  </Box>
                </Card>
              </Grid>

              {filteredFilters.map((filter) => {
                const iconSrc = filter.IconUrl || filter.Icon;
                return (
                  <Grid item key={filter.Id} xs={6} sm={6} md={4} lg={3}>
                    <Card onClick={() => handleFilterSelect(filter.Id)} elevation={0} sx={{ height: { xs: 180, sm: 220, md: 260, lg: 280 }, display: 'flex', flexDirection: 'column', cursor: 'pointer', transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', borderRadius: { xs: 2, sm: 2.5, md: 3 }, overflow: 'hidden', border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 24px rgba(0,0,0,0.15)', borderColor: 'primary.main' }, '&:active': { transform: 'scale(0.98)' } }}>
                      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.50', p: { xs: 1.5, sm: 2, md: 2.5, lg: 3 } }}>
                        {iconSrc ? (
                          <Avatar src={iconSrc} alt={filter.name} variant="rounded" sx={{ width: { xs: 60, sm: 80, md: 100, lg: 120 }, height: { xs: 60, sm: 80, md: 100, lg: 120 }, objectFit: 'contain' }} />
                        ) : (
                          <Avatar variant="rounded" sx={{ width: { xs: 60, sm: 80, md: 100, lg: 120 }, height: { xs: 60, sm: 80, md: 100, lg: 120 }, bgcolor: 'primary.light', fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem', lg: '3rem' }, fontWeight: 700 }}>
                            {filter.name.charAt(0).toUpperCase()}
                          </Avatar>
                        )}
                      </Box>
                      <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5 }, textAlign: 'center', bgcolor: 'background.paper' }}>
                        <Typography variant="h6" fontWeight="700" sx={{ fontSize: { xs: '0.85rem', sm: '1rem', md: '1.1rem', lg: '1.25rem' }, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', lineHeight: 1.3 }}>
                          {filter.name}
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            {filteredFilters.length === 0 && filterSearch && (
              <Box sx={{ textAlign: 'center', py: { xs: 6, sm: 7, md: 8 } }}>
                <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.25rem' } }}>
                  {t('storeSelection.noFiltersFound', 'No categories found')}
                </Typography>
              </Box>
            )}
          </>
        )}

        {/* ============= STORES VIEW ============= */}
        {view === 'stores' && (
          <>
            <Box sx={{ mb: { xs: 2, sm: 2.5, md: 3 } }}>
              <Button startIcon={<ArrowBackIcon sx={{ fontSize: { xs: 18, sm: 20, md: 22 } }} />} onClick={handleBackToFilters} variant="outlined" size={isMobile ? "medium" : "large"} sx={{ borderRadius: { xs: 1.5, sm: 2 }, px: { xs: 2, sm: 2.5, md: 3 }, py: { xs: 1, sm: 1.2, md: 1.5 }, fontWeight: 600, fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1rem' }, '&:active': { transform: 'scale(0.98)' } }}>
                {t('common.back', 'Back to Categories')}
              </Button>
            </Box>

            {selectedFilter === null && featuredBranches.length > 0 && (
              <Box sx={{ mb: { xs: 3, sm: 4 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 1.5, sm: 2 }, px: { xs: 1, sm: 0 } }}>
                  <StarIcon sx={{ color: '#F59E0B', fontSize: { xs: 20, sm: 24 } }} />
                  <Typography variant="h6" sx={{ fontWeight: 800, fontSize: { xs: '1rem', sm: '1.2rem' } }}>
                    {t('branch.featuredStores', '⭐ Featured Stores')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: { xs: 1.5, sm: 2 }, overflowX: 'auto', pb: 1, px: { xs: 1, sm: 0 }, '&::-webkit-scrollbar': { height: 6 }, '&::-webkit-scrollbar-thumb': { bgcolor: alpha(theme.palette.primary.main, 0.2), borderRadius: 3 } }}>
                  {featuredBranches.map((fb) => {
                    const fbImages = getImageUrls(fb);
                    const fbRating = fb.averageRating || fb.AverageRating || 0;
                    return (
                      <Card key={`featured-store-${fb.id || fb.Id}`} onClick={() => handleSelectStore(fb)} sx={{ minWidth: { xs: 140, sm: 180 }, maxWidth: { xs: 140, sm: 180 }, cursor: 'pointer', borderRadius: 2, overflow: 'hidden', position: 'relative', flexShrink: 0, border: '2px solid #F59E0B', transition: 'all 0.3s', '&:hover': { transform: 'translateY(-4px)', boxShadow: `0 6px 20px ${alpha('#F59E0B', 0.25)}` } }}>
                        <Box sx={{ position: 'relative', width: '100%', paddingTop: '65%', overflow: 'hidden' }}>
                          <CardMedia component="img" image={fbImages.medium} alt={fb.name || fb.Name} loading="lazy" sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                          <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(transparent, rgba(0,0,0,0.7))', p: 1 }}>
                            <Typography sx={{ color: 'white', fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.85rem' }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {fb.name || fb.Name}
                            </Typography>
                            {fbRating > 0 && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mt: 0.2 }}>
                                <StarIcon sx={{ fontSize: 11, color: '#F59E0B' }} />
                                <Typography sx={{ color: 'white', fontSize: '0.6rem', fontWeight: 600 }}>{fbRating.toFixed(1)}</Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Card>
                    );
                  })}
                </Box>
              </Box>
            )}

            <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 4, md: 5 } }}>
              <Typography variant="h3" component="h1" gutterBottom fontWeight="800" color="primary" sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem', lg: '3rem' }, mb: { xs: 1, sm: 1.5, md: 2 }, px: { xs: 1, sm: 0 } }}>
                {selectedFilter ? filters.find(f => f.Id === selectedFilter)?.name || t('storeSelection.title', 'Select a Store') : t('storeSelection.allStores', 'All Stores')}
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem', lg: '1.25rem' }, mb: { xs: 2, sm: 2.5, md: 3 }, fontWeight: 400, px: { xs: 1, sm: 0 } }}>
                {t('storeSelection.subtitle', 'Please choose the nearest store for the best service')}
              </Typography>
              <Box sx={{ mt: { xs: 2, sm: 2.5, md: 3 }, maxWidth: { xs: '100%', sm: 500 }, mx: 'auto', width: '100%', px: { xs: 1, sm: 0 } }}>
                <TextField
                  fullWidth variant="outlined" value={storeSearch} onChange={e => setStoreSearch(e.target.value)}
                  placeholder={t('storeSelection.searchPlaceholder', 'Search stores by name...')} aria-label={t('storeSelection.searchAria', 'Search stores by name')}
                  size={isMobile ? "small" : "medium"} autoComplete="off"
                  sx={{ backgroundColor: 'background.paper', borderRadius: { xs: 2, sm: 2.5, md: 3 }, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', '& .MuiOutlinedInput-root': { borderRadius: { xs: 2, sm: 2.5, md: 3 }, fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' }, fontWeight: 500, bgcolor: 'background.paper', transition: 'all 0.3s', '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.12)' }, '&.Mui-focused': { boxShadow: '0 4px 16px rgba(0,0,0,0.15)', borderColor: 'primary.main' } }, '& input': { py: { xs: 1.2, sm: 1.5, md: 2 } } }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon color="primary" sx={{ fontSize: { xs: 20, sm: 24, md: 28 } }} /></InputAdornment>,
                    endAdornment: storeSearch && <InputAdornment position="end"><IconButton aria-label={t('storeSelection.clearSearch', 'Clear search')} onClick={() => setStoreSearch('')} edge="end" size={isMobile ? "small" : "medium"}><ClearIcon sx={{ fontSize: { xs: 18, sm: 22 } }} /></IconButton></InputAdornment>
                  }}
                />
              </Box>
            </Box>

            <Grid container spacing={{ xs: 1, sm: 2.5, md: 3 }} sx={{ direction: 'rtl', justifyContent: 'center' }}>
              {filteredStores.length === 0 && selectedFilter != null && (
                <Grid item xs={12}><Alert severity="info" sx={{ borderRadius: { xs: 1.5, sm: 2 } }}>{t('storeSelection.noStoresForFilter', 'No stores found for the selected category.')}</Alert></Grid>
              )}

              {filteredStores.map((store) => {
                const imageUrls = getImageUrls(store);
                const description = store.description || store.Description || '';
                const cities = branchCities[store.id || store.Id] || [];
                const storeId = store.id || store.Id;
                const isFeatured = store.isFeatured || store.IsFeatured || false;
                const avgRating = store.averageRating || store.AverageRating || 0;
                const totalRatings = store.totalRatings || store.TotalRatings || 0;
                const totalLikes = store.totalLikes || store.TotalLikes || 0;
                const totalDislikes = store.totalDislikes || store.TotalDislikes || 0;
                const userLikeStatus = userLikes[storeId] || null;

                return (
                  <Grid item key={store.Id || store.id || store._id}>
                    <Card sx={{ width: { xs: 140, sm: 200, md: 220 }, height: '100%', borderRadius: { xs: 1.5, sm: 2 }, overflow: 'visible', boxShadow: `0 ${isMobile ? '3px 10px' : '8px 24px'} ${alpha(theme.palette.common.black, 0.08)}`, border: isFeatured ? `2px solid #F59E0B` : `1px solid ${alpha(theme.palette.divider, 0.1)}`, bgcolor: 'background.paper', transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)', position: 'relative', '&:hover': { transform: isMobile ? 'scale(0.98)' : 'translateY(-12px)', boxShadow: `0 ${isMobile ? '5px 14px' : '20px 40px'} ${alpha(theme.palette.primary.main, 0.2)}`, border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`, '& .branch-image': { transform: 'scale(1.1)' } }, '&:active': { transform: isMobile ? 'scale(0.95)' : 'translateY(-12px)' } }}>

                      {isFeatured && (
                        <Chip
                          icon={<StarIcon sx={{ fontSize: 12, color: 'white !important' }} />}
                          label={t('branch.featured', '⭐ Featured')}
                          size="small"
                          sx={{ position: 'absolute', top: { xs: 6, sm: 10 }, left: { xs: 6, sm: 10 }, zIndex: 11, bgcolor: '#F59E0B', color: 'white', fontWeight: 700, fontSize: { xs: '0.55rem', sm: '0.65rem' }, height: { xs: 18, sm: 22 }, '& .MuiChip-icon': { ml: 0.3 } }}
                        />
                      )}

                      <Box sx={{ position: 'absolute', top: { xs: 8, sm: 12 }, right: { xs: 8, sm: 12 }, zIndex: 10, display: 'flex', gap: { xs: 0.5, sm: 1 } }}>
                        <Tooltip title={t('branch.share', 'Share')} arrow>
                          <IconButton onClick={(e) => { e.stopPropagation(); handleShare(store); }} sx={{ bgcolor: 'white', boxShadow: 2, width: { xs: 28, sm: 36 }, height: { xs: 28, sm: 36 }, '&:hover': { bgcolor: 'primary.main', color: 'white', transform: 'scale(1.1)' } }}>
                            <ShareIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('branch.qrCode', 'QR Code')} arrow>
                          <IconButton onClick={(e) => { e.stopPropagation(); handleOpenQRModal(store); }} sx={{ bgcolor: 'white', boxShadow: 2, width: { xs: 28, sm: 36 }, height: { xs: 28, sm: 36 }, '&:hover': { bgcolor: 'secondary.main', color: 'white', transform: 'scale(1.1)' } }}>
                            <QrCode2Icon sx={{ fontSize: { xs: 16, sm: 20 } }} />
                          </IconButton>
                        </Tooltip>
                      </Box>

                      <CardActionArea onClick={() => handleSelectStore(store)} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', height: '100%' }}>
                        <Box sx={{ position: 'relative', width: '100%', paddingTop: '100%', overflow: 'hidden', bgcolor: alpha(theme.palette.primary.main, 0.03), borderRadius: '12px 12px 0 0' }}>
                          <CardMedia component="img" image={imageUrls.medium} srcSet={`${imageUrls.thumbnail} 150w, ${imageUrls.medium} 400w, ${imageUrls.large} 1200w`} sizes={isMobile ? "140px" : "(max-width: 960px) 200px, 220px"} alt={store.name || store.Name} loading="lazy" className="branch-image" sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)', cursor: 'pointer' }} />
                        </Box>
                        <CardContent sx={{ p: { xs: 1, sm: 2 }, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="h6" sx={{ fontSize: { xs: '11px', sm: '15px', md: '16px' }, fontWeight: 700, color: theme.palette.text.primary, mb: { xs: 0.2, sm: 0.5 }, textAlign: 'center', lineHeight: 1.3, height: { xs: '2.3em', sm: '2.6em' }, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                            {store.name || store.Name}
                          </Typography>
                          {description && (
                            <Typography variant="body2" sx={{ fontSize: { xs: '8px', sm: '11px' }, color: theme.palette.text.secondary, mb: { xs: 0.8, sm: 1.5 }, textAlign: 'center', lineHeight: 1.3, height: { xs: '2.3em', sm: '2.8em' }, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                              {description}
                            </Typography>
                          )}

                          {(avgRating > 0 || totalRatings > 0) && (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: { xs: 0.5, sm: 1 } }}>
                              <Rating value={avgRating} precision={0.5} readOnly size="small" sx={{ fontSize: { xs: '0.7rem', sm: '0.9rem' } }} />
                              {totalRatings > 0 && (
                                <Typography sx={{ fontSize: { xs: '7px', sm: '10px' }, color: 'text.secondary' }}>({totalRatings})</Typography>
                              )}
                            </Box>
                          )}

                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: { xs: 0.5, sm: 1 }, mb: { xs: 0.5, sm: 1 }, flexWrap: 'nowrap' }}>
                            <Tooltip title={t('branch.like', 'Like')} arrow>
                              <IconButton
                                onClick={(e) => { e.stopPropagation(); handleLike(store, 'like'); }}
                                size="small"
                                sx={{ p: { xs: 0.3, sm: 0.5 }, bgcolor: userLikeStatus === 'like' ? alpha('#10B981', 0.12) : alpha(theme.palette.divider, 0.15), '&:hover': { bgcolor: alpha('#10B981', 0.2) } }}
                              >
                                {userLikeStatus === 'like'
                                  ? <ThumbUpIcon sx={{ fontSize: { xs: 11, sm: 14 }, color: '#10B981' }} />
                                  : <ThumbUpOutlinedIcon sx={{ fontSize: { xs: 11, sm: 14 }, color: 'text.secondary' }} />
                                }
                              </IconButton>
                            </Tooltip>
                            {totalLikes > 0 && <Typography sx={{ fontSize: { xs: '7px', sm: '9px' }, color: userLikeStatus === 'like' ? '#10B981' : 'text.secondary', fontWeight: 600 }}>{totalLikes}</Typography>}

                            <Tooltip title={t('branch.dislike', 'Dislike')} arrow>
                              <IconButton
                                onClick={(e) => { e.stopPropagation(); handleLike(store, 'dislike'); }}
                                size="small"
                                sx={{ p: { xs: 0.3, sm: 0.5 }, bgcolor: userLikeStatus === 'dislike' ? alpha('#EF4444', 0.12) : alpha(theme.palette.divider, 0.15), '&:hover': { bgcolor: alpha('#EF4444', 0.2) } }}
                              >
                                {userLikeStatus === 'dislike'
                                  ? <ThumbDownIcon sx={{ fontSize: { xs: 11, sm: 14 }, color: '#EF4444' }} />
                                  : <ThumbDownOutlinedIcon sx={{ fontSize: { xs: 11, sm: 14 }, color: 'text.secondary' }} />
                                }
                              </IconButton>
                            </Tooltip>
                            {totalDislikes > 0 && <Typography sx={{ fontSize: { xs: '7px', sm: '9px' }, color: userLikeStatus === 'dislike' ? '#EF4444' : 'text.secondary', fontWeight: 600 }}>{totalDislikes}</Typography>}

                            <Tooltip title={t('branch.rate', 'Rate')} arrow>
                              <IconButton
                                onClick={(e) => { e.stopPropagation(); handleOpenRatingModal(store); }}
                                size="small"
                                sx={{ p: { xs: 0.3, sm: 0.5 }, bgcolor: alpha(theme.palette.primary.main, 0.08), '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.15) } }}
                              >
                                <StarBorderIcon sx={{ fontSize: { xs: 11, sm: 14 }, color: 'primary.main' }} />
                              </IconButton>
                            </Tooltip>
                          </Box>

                          <Box sx={{ mb: { xs: 0.8, sm: 1.5 }, p: { xs: 0.5, sm: 1 }, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: { xs: 1, sm: 2 }, border: `1px dashed ${alpha(theme.palette.primary.main, 0.2)}` }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.3, sm: 0.5 }, mb: { xs: 0.2, sm: 0.3 }, justifyContent: 'center' }}>
                              <LocationOnIcon sx={{ fontSize: { xs: 9, sm: 12 }, color: 'primary.main' }} />
                              <Typography variant="caption" sx={{ color: theme.palette.primary.main, fontWeight: 700, fontSize: { xs: '7px', sm: '10px' }, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                {t('branch.address', 'Address')}
                              </Typography>
                            </Box>
                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: { xs: '7px', sm: '10px' }, textAlign: 'center', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                              {store.address || store.Address || t('common.noAddress', 'No Address')}
                            </Typography>
                          </Box>

                          {cities.length > 0 && (
                            <Box sx={{ mb: { xs: 0.8, sm: 1.5 }, p: { xs: 0.5, sm: 1 }, bgcolor: alpha(theme.palette.success.main, 0.05), borderRadius: { xs: 1, sm: 2 }, border: `1px dashed ${alpha(theme.palette.success.main, 0.2)}` }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.3, sm: 0.5 }, mb: { xs: 0.2, sm: 0.3 }, justifyContent: 'center' }}>
                                <LocalShippingIcon sx={{ fontSize: { xs: 9, sm: 12 }, color: 'success.main' }} />
                                <Typography variant="caption" sx={{ color: theme.palette.success.main, fontWeight: 700, fontSize: { xs: '7px', sm: '10px' }, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                  {t('branch.delivery', 'Delivery')}
                                </Typography>
                              </Box>
                              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: { xs: '7px', sm: '10px' }, textAlign: 'center', lineHeight: 1.2, display: 'block' }}>
                                {cities.length >= 5 ? `🚚 ${t('branch.deliversToAllCities', 'Delivers to all cities')}` : cities.map(c => c.Name || c.name).join(' • ')}
                              </Typography>
                            </Box>
                          )}

                          <Box sx={{ flexGrow: 1 }} />
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: { xs: 0.4, sm: 1 }, mb: { xs: 1, sm: 2 } }}>
                            <Typography sx={{ color: theme.palette.text.secondary, fontSize: { xs: '8px', sm: '12px' }, fontWeight: 600 }}>
                              🕒 {t('branch.allDaysOfWeek', 'All Days')} - {t('branch.twentyFourHours', '24 Hours')}
                            </Typography>
                          </Box>
                          <Button variant="contained" fullWidth sx={{ bgcolor: theme.palette.primary.main, borderRadius: { xs: 1.5, sm: 3 }, color: theme.palette.primary.contrastText, py: { xs: 0.7, sm: 1.2 }, fontSize: { xs: '9px', sm: '13px' }, fontWeight: 700, textTransform: 'none', boxShadow: `0 ${isMobile ? '2px 6px' : '4px 12px'} ${alpha(theme.palette.primary.main, 0.3)}`, '&:hover': { bgcolor: theme.palette.primary.dark, boxShadow: `0 ${isMobile ? '3px 10px' : '6px 16px'} ${alpha(theme.palette.primary.main, 0.4)}`, transform: isMobile ? 'none' : 'translateY(-2px)' }, '&:active': { transform: 'scale(0.95)' }, transition: 'all 0.3s ease' }}>
                            {t('storeSelection.selectStore', 'Select Store')}
                          </Button>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            {filteredStores.length === 0 && storeSearch && (
              <Box sx={{ textAlign: 'center', py: { xs: 6, sm: 7, md: 8 } }}>
                <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.25rem' } }}>
                  {t('storeSelection.noResults', 'No stores found matching your search')}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Container>

      {/* ============= QR MODAL (UNCHANGED) ============= */}
      <Modal open={qrModalOpen} onClose={() => setQrModalOpen(false)} closeAfterTransition BackdropComponent={Backdrop} BackdropProps={{ timeout: 500, sx: { backdropFilter: 'blur(8px)', backgroundColor: alpha(theme.palette.common.black, 0.7), zIndex: 1300 } }} sx={{ zIndex: 1300 }}>
        <Fade in={qrModalOpen}>
          <Box sx={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: { xs: '90%', sm: 500, md: 600 }, maxHeight: { xs: '85vh', sm: '85vh' }, marginTop: { xs: '60px', sm: '80px', md: '100px' }, overflowY: 'auto', bgcolor: 'background.paper', borderRadius: { xs: 2, sm: 3, md: 4 }, boxShadow: 24, p: { xs: 3, sm: 4, md: 5 }, outline: 'none', zIndex: 1301, '&::-webkit-scrollbar': { width: '8px' }, '&::-webkit-scrollbar-track': { bgcolor: alpha(theme.palette.divider, 0.1), borderRadius: '10px' }, '&::-webkit-scrollbar-thumb': { bgcolor: alpha(theme.palette.primary.main, 0.3), borderRadius: '10px', '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.5) } } }}>
            {selectedBranchForQR && (
              <>
                <IconButton onClick={() => setQrModalOpen(false)} sx={{ position: 'absolute', top: { xs: 12, sm: 16 }, right: { xs: 12, sm: 16 }, bgcolor: alpha(theme.palette.error.main, 0.1), zIndex: 1, '&:hover': { bgcolor: theme.palette.error.main, color: 'white' } }}>
                  <CloseIcon />
                </IconButton>
                <Typography variant="h5" sx={{ fontWeight: 800, color: 'primary.main', textAlign: 'center', mb: 1, fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' }, pr: { xs: 5, sm: 6 } }}>
                  {selectedBranchForQR.name || selectedBranchForQR.Name}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', mb: 3, fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>
                  {t('branch.shareQRTitle', 'Share this branch with others')}
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3, p: { xs: 2, sm: 3 }, bgcolor: 'white', borderRadius: 2, boxShadow: 3 }}>
                  <QRCodeCanvas id="qr-code-canvas" value={generateBranchLink(selectedBranchForQR.Id || selectedBranchForQR.id)} size={isMobile ? 200 : 256} level="H" includeMargin={true} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 2, mb: 3, border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}` }}>
                  <LinkIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                  <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                    {generateBranchLink(selectedBranchForQR.Id || selectedBranchForQR.id)}
                  </Typography>
                  <IconButton onClick={() => handleCopyLink(selectedBranchForQR)} size="small" sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}>
                    <ContentCopyIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
                <Stack spacing={2}>
                  <Button fullWidth variant="contained" startIcon={<DownloadIcon />} onClick={handleDownloadQR} sx={{ py: { xs: 1.2, sm: 1.5 }, fontSize: { xs: '0.9rem', sm: '1rem' }, fontWeight: 700, borderRadius: 2, textTransform: 'none' }}>
                    {t('branch.downloadQR', 'Download QR Code')}
                  </Button>
                  <Divider><Typography variant="caption" sx={{ color: 'text.secondary', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>{t('branch.orShareVia', 'Or share via')}</Typography></Divider>
                  <Stack direction="row" spacing={2} sx={{ justifyContent: 'center' }}>
                    <Tooltip title="WhatsApp" arrow><IconButton onClick={() => handleShareWhatsApp(selectedBranchForQR)} sx={{ bgcolor: '#25D366', color: 'white', width: { xs: 48, sm: 56 }, height: { xs: 48, sm: 56 }, '&:hover': { bgcolor: '#128C7E', transform: 'scale(1.1)' } }}><WhatsAppIcon sx={{ fontSize: { xs: 24, sm: 28 } }} /></IconButton></Tooltip>
                    <Tooltip title="Facebook" arrow><IconButton onClick={() => handleShareFacebook(selectedBranchForQR)} sx={{ bgcolor: '#1877F2', color: 'white', width: { xs: 48, sm: 56 }, height: { xs: 48, sm: 56 }, '&:hover': { bgcolor: '#166FE5', transform: 'scale(1.1)' } }}><FacebookIcon sx={{ fontSize: { xs: 24, sm: 28 } }} /></IconButton></Tooltip>
                    <Tooltip title="Twitter" arrow><IconButton onClick={() => handleShareTwitter(selectedBranchForQR)} sx={{ bgcolor: '#1DA1F2', color: 'white', width: { xs: 48, sm: 56 }, height: { xs: 48, sm: 56 }, '&:hover': { bgcolor: '#1A91DA', transform: 'scale(1.1)' } }}><TwitterIcon sx={{ fontSize: { xs: 24, sm: 28 } }} /></IconButton></Tooltip>
                    <Tooltip title={t('branch.copyLink', 'Copy Link')} arrow><IconButton onClick={() => handleCopyLink(selectedBranchForQR)} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main', width: { xs: 48, sm: 56 }, height: { xs: 48, sm: 56 }, '&:hover': { bgcolor: 'primary.main', color: 'white', transform: 'scale(1.1)' } }}><ContentCopyIcon sx={{ fontSize: { xs: 24, sm: 28 } }} /></IconButton></Tooltip>
                  </Stack>
                </Stack>
              </>
            )}
          </Box>
        </Fade>
      </Modal>

      {/* ============= RATING MODAL ============= */}
      <Modal open={ratingModalOpen} onClose={() => setRatingModalOpen(false)} closeAfterTransition BackdropComponent={Backdrop} BackdropProps={{ timeout: 400, sx: { backdropFilter: 'blur(6px)', backgroundColor: alpha(theme.palette.common.black, 0.5) } }}>
        <Fade in={ratingModalOpen}>
          <Box sx={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: { xs: '88%', sm: 440, md: 500 }, bgcolor: 'background.paper', borderRadius: { xs: 3, sm: 4 }, boxShadow: 24, p: { xs: 3, sm: 4, md: 5 }, outline: 'none', textAlign: 'center' }}>
            <IconButton onClick={() => setRatingModalOpen(false)} sx={{ position: 'absolute', top: 12, right: 12, bgcolor: alpha(theme.palette.error.main, 0.1), '&:hover': { bgcolor: theme.palette.error.main, color: 'white' } }}>
              <CloseIcon />
            </IconButton>

            <Box sx={{ width: { xs: 64, sm: 80 }, height: { xs: 64, sm: 80 }, borderRadius: '50%', bgcolor: alpha('#F59E0B', 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
              <StarIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: '#F59E0B' }} />
            </Box>

            <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5, fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
              {t('branch.rateStore', 'Rate this Store')}
            </Typography>

            {ratingBranch && (
              <Typography variant="body1" sx={{ color: 'primary.main', fontWeight: 600, mb: 3, fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
                {ratingBranch.name || ratingBranch.Name}
              </Typography>
            )}

            <Box sx={{ mb: 1 }}>
              <Rating
                value={ratingValue}
                onChange={(event, newValue) => setRatingValue(newValue || 0)}
                size={isMobile ? "large" : "large"}
                precision={1}
                sx={{ fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' } }}
              />
            </Box>

            {ratingValue > 0 && (
              <Typography sx={{ fontSize: { xs: '1.1rem', sm: '1.3rem' }, fontWeight: 700, mb: 2 }}>
                {ratingValue === 1 ? '😞' : ratingValue === 2 ? '😐' : ratingValue === 3 ? '🙂' : ratingValue === 4 ? '😊' : '🤩'}{' '}
                {ratingValue}/5
              </Typography>
            )}

            <TextField
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              placeholder={t('branch.ratingComment', 'Add a comment (optional)...')}
              value={ratingComment}
              onChange={(e) => setRatingComment(e.target.value)}
              inputProps={{ maxLength: 500 }}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  bgcolor: alpha(theme.palette.background.default, 0.5),
                },
              }}
            />

            <Button
              fullWidth
              variant="contained"
              disabled={ratingValue === 0 || submittingRating}
              onClick={handleSubmitRating}
              startIcon={submittingRating ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
              sx={{
                py: { xs: 1.3, sm: 1.5 },
                fontSize: { xs: '0.95rem', sm: '1.05rem' },
                fontWeight: 700,
                borderRadius: 2,
                textTransform: 'none',
                boxShadow: ratingValue === 0 ? 'none' : `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                '&:hover': {
                  boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
                  transform: 'translateY(-2px)',
                },
                '&:active': { transform: 'scale(0.98)' },
                transition: 'all 0.3s ease',
              }}
            >
              {submittingRating
                ? t('common.submitting', 'Submitting...')
                : t('branch.submitRating', 'Submit Rating')
              }
            </Button>
          </Box>
        </Fade>
      </Modal>

      {/* ============= CITY NOT SERVED MODAL ============= */}
      <Modal open={cityModalOpen} onClose={() => setCityModalOpen(false)} closeAfterTransition BackdropComponent={Backdrop} BackdropProps={{ timeout: 400, sx: { backdropFilter: 'blur(6px)', backgroundColor: alpha(theme.palette.common.black, 0.5) } }}>
        <Fade in={cityModalOpen}>
          <Box sx={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: { xs: '88%', sm: 440, md: 500 }, bgcolor: 'background.paper', borderRadius: { xs: 3, sm: 4 }, boxShadow: 24, p: { xs: 3, sm: 4, md: 5 }, outline: 'none', textAlign: 'center' }}>
            <Box sx={{ width: { xs: 72, sm: 90 }, height: { xs: 72, sm: 90 }, borderRadius: '50%', bgcolor: alpha('#F59E0B', 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: { xs: 2, sm: 3 } }}>
              <LocalShippingIcon sx={{ fontSize: { xs: 36, sm: 44 }, color: '#F59E0B' }} />
            </Box>

            <Typography variant="h5" sx={{ fontWeight: 800, mb: 1, fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.6rem' } }}>
              {t('branch.noDeliveryTitle', 'Delivery Not Available')}
            </Typography>

            <Typography variant="body1" sx={{ color: 'text.secondary', mb: { xs: 2, sm: 3 }, fontSize: { xs: '0.9rem', sm: '1rem' }, lineHeight: 1.6 }}>
              {t('branch.noDeliveryMessage', 'Sorry, {{storeName}} does not deliver to your city.', { storeName: blockedStoreName })}
            </Typography>

            {(() => {
              const storeId = branches.find(b => (b.name || b.Name) === blockedStoreName)?.id || branches.find(b => (b.name || b.Name) === blockedStoreName)?.Id;
              const cities = storeId ? (branchCities[storeId] || []) : [];
              if (cities.length === 0) return null;
              const cityNames = cities.map(c => c.Name || c.name);
              return (
                <Box sx={{ bgcolor: alpha('#3B82F6', 0.06), borderRadius: 2, p: { xs: 1.5, sm: 2 }, mb: { xs: 2, sm: 3 }, border: `1px dashed ${alpha('#3B82F6', 0.15)}` }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: { xs: '0.8rem', sm: '0.9rem' }, lineHeight: 1.5, mb: 1 }}>
                    {cities.length === 1
                      ? t('branch.ifInCity', 'If you are currently in {{cityName}},', { cityName: cityNames[0] })
                      : t('branch.ifInCities', 'If you are currently in one of these cities: {{cityNames}},', { cityNames: cityNames.join(' • ') })
                    }
                  </Typography>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => { setCityModalOpen(false); navigate('/profile'); }}
                    sx={{ fontWeight: 700, fontSize: { xs: '0.8rem', sm: '0.9rem' }, textDecoration: 'underline' }}
                  >
                    {t('branch.clickToChangeCity', 'Click here to change your city')} →
                  </Button>
                </Box>
              );
            })()}

            <Typography variant="body2" sx={{ color: 'text.secondary', mb: { xs: 2, sm: 3 }, fontSize: { xs: '0.85rem', sm: '0.95rem' }, lineHeight: 1.5 }}>
              {t('branch.noDeliveryHint', 'Please choose another store and start shopping!')}
            </Typography>

            <Divider sx={{ mb: { xs: 2, sm: 3 } }} />

            <Button
              fullWidth
              variant="contained"
              onClick={() => setCityModalOpen(false)}
              sx={{
                py: { xs: 1.3, sm: 1.5 },
                fontSize: { xs: '0.95rem', sm: '1.05rem' },
                fontWeight: 700,
                borderRadius: 2,
                textTransform: 'none',
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                '&:hover': { boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}` },
              }}
            >
              {t('branch.chooseAnotherStore', 'Choose Another Store')}
            </Button>
          </Box>
        </Fade>
      </Modal>

      {/* ============= SNACKBAR ============= */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', borderRadius: { xs: 2, sm: 3 }, fontWeight: 600, fontSize: { xs: '0.85rem', sm: '0.95rem' } }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default BranchSelectionPage;